import { CurrencyPipe } from '@angular/common';
import { Component, computed, inject, input, OnInit, output, signal, ChangeDetectionStrategy } from '@angular/core';
import { ETipoDocumentoSUNAT } from '@core/enums/tipoDocumentoSUNAT.enum';
import { IProductoCarritoData } from '@core/interfaces/entidades/productoCarrito.entidad';
import { CarritoCompraService } from '@shared/services/carrito-compra.service';
import { FormsModule } from '@angular/forms';
import { TipoPagoService } from '@shared/services/tipo-pago.service';
import { ITipoPagoData } from '@core/interfaces/entidades/tipoPago.entidad';
import { BuscadorCliente } from '../buscador-cliente/buscador-cliente';
import { UiButton, UiProgressSpinner, UiTooltip } from '@shared/components/ui';
import { ToastService } from '@shared/services/toast.service';
import { VentaApiService } from '@core/services/api/venta-api.service';
import {
  IRealizarVentaCreditoReq,
  IRealizarVentaReq,
} from '@core/interfaces/request-response/venta.dto.interface';
import { IClienteData } from '@core/interfaces/entidades/cliente.entidad';
import { form, FormField } from '@angular/forms/signals';
import { AgregarPago } from '../agregar-pago/agregar-pago';
import { IAgregarPagoComponent } from '@core/interfaces/components/agregarPago.component.interface';
import { CreditoApiService } from '@core/services/api/credito-api.service';
import {
  CreatePrintJobRq,
  ImpresoraDatosVentaRq,
} from '@core/interfaces/request-response/impresora.dto.interface';
import { ETipoImpresion } from '@core/enums/impresora.enum';
import { ImpresoraService } from '@shared/services/impresora.service';
import { ImpresoraApiService } from '@core/services/api/impresora-api.service';
import {
  logger,
  redondearDosDecimales,
  formatearErrorStock,
  buildProductoCarritoToProductoComprado,
} from '@shared/utils/functions.utils';
import { SucursalService } from '@shared/services/sucursal.service';
import { environment } from '@env/environment';
import { LocalStorageService } from '@shared/services/local-storage.service';
import { ID_DESCARGA_PDF } from '@shared/constants/impresora.const';
import { EmpresaService } from '@shared/services/empresa.service';
import {
  buildImpresion,
  obtenerFooterMensajePorDocumento,
} from '@shared/utils/formatos_impresion.utils';
import { QzService } from '@shared/services/qz.service';
import { ETipoSistemasEmpresa } from '@core/enums/tipoSistemasEmpresa.enum';
import { WhatsAppPhoneInput } from '../whatsapp-phone-input/whatsapp-phone-input';
import { IVentaData } from '@core/interfaces/entidades/venta.entidad';
/** Pago capturado en el POS antes de enviarse al backend. */
export interface IPagoPendiente {
  tipo_pago: ITipoPagoData;
  monto: number;
}

@Component({
  selector: 'app-procesar-venta',
  imports: [
    CurrencyPipe,
    FormsModule,
    BuscadorCliente,
    UiButton,
    UiTooltip,
    FormField,
    AgregarPago,
    UiProgressSpinner,
    WhatsAppPhoneInput,
  ],
  templateUrl: './procesar-venta.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './procesar-venta.css',
})
export class ProcesarVenta implements OnInit {
  private readonly impresoraService = inject(ImpresoraService);
  private readonly impresoraApiService = inject(ImpresoraApiService);
  private readonly toastService = inject(ToastService);
  private readonly sucursalService = inject(SucursalService);
  private readonly localStorageService = inject(LocalStorageService);
  private readonly empresaService = inject(EmpresaService);
  private readonly qzService = inject(QzService);

  private readonly STORAGE_KEY_PENDING_SALE = 'ventaclick_pending_sale_idempotency_key';
  private readonly MAX_KEY_AGE_MS = 5 * 60 * 1000;

  //#region Propiedades y Métodos de Pago
  procesandoVenta = signal(false);
  idempotencyKey = signal<string | null>(null);
  impresoraSeleccionada = signal<number | null>(null);
  productosCarrito: IProductoCarritoData[] = [];
  tiposPago: ITipoPagoData[] = [];
  pagosRealizados = signal<IPagoPendiente[]>([]);
  tipo_documentos = Object.values(ETipoDocumentoSUNAT).filter((tipo) => {
    if (tipo === ETipoDocumentoSUNAT.NOTA_CREDITO) return false;
    if (
      (tipo === ETipoDocumentoSUNAT.FACTURA || tipo === ETipoDocumentoSUNAT.BOLETA) &&
      !this.empresaService
        .getEmpresaData()
        ?.sistemas_activos?.includes(ETipoSistemasEmpresa.FACTURACION)
    ) {
      return false;
    }
    return true;
  });
  descuento = signal<{ valor: number; efectivo: boolean }>({ valor: 0, efectivo: true });

  mesaId = input<number | undefined>(undefined);
  gestionTiempoId = input<number | undefined>(undefined);
  sendWhatsApp = signal(false);
  whatsAppCountryCode = signal('+51');
  whatsAppNumber = signal('');
  disableWhatsApp = computed(() => {
    const cliente = this.formProcesarVenta().controlValue().cliente;
    if (!cliente) return true;
    return !cliente.numero_celular;
  });
  showWhatsAppPhoneInput = computed(() => {
    return this.sendWhatsApp() && this.disableWhatsApp();
  });
  //#endregion
  //#region datos para html
  optionsTipoPago = computed(() => {
    return this.tiposPago.map((tipoPago) => ({ label: tipoPago.nombre, value: tipoPago }));
  });
  formProcesarVenta = form(
    signal({
      tipo_documento:
        this.localStorageService.getUltimoTipoDocumentoSUNAT() || ETipoDocumentoSUNAT.NOTA_VENTA,
      tipoPago: {} as ITipoPagoData,
      montoTipoPago: 0,
      observaciones: '',
      cliente: null as IClienteData | null,
    }),
  );
  //#endregion
  //#region Lógica de selección de tipo de pago y documento

  readonly opcionesIgv = environment.igvOptions.map((igv) => {
    const label = `${(igv * 100).toFixed(2).replace(/\.00$/, '').replace(/0$/, '')}%`;
    return { label, valor: igv };
  });
  igvPorcentaje = signal<number>(0.18);

  selectTipoDocumento(tipo_documento: ETipoDocumentoSUNAT) {
    this.formProcesarVenta().controlValue().tipo_documento = tipo_documento;
    this.localStorageService.setUltimoTipoDocumentoSUNAT(tipo_documento);
  }

  totalBruto = computed(() => {
    const total = this.productosCarrito.reduce(
      (sum, producto) => sum + producto.precio * producto.cantidad,
      0,
    );
    return redondearDosDecimales(total);
  });
  total = computed(() => {
    const totalSinDescuento = this.totalBruto();
    const descuento = this.descuentoFinal();
    return redondearDosDecimales(totalSinDescuento - descuento);
  });

  descuentoFinal = computed(() => {
    if (this.descuento().efectivo) {
      return this.descuento().valor;
    } else {
      const descuento = this.totalBruto() * (this.descuento().valor / 100);
      return parseFloat(descuento.toFixed(2));
    }
  });

  // pagado
  totalPagado = computed(() => {
    return redondearDosDecimales(this.pagosRealizados().reduce((sum, pago) => sum + pago.monto, 0));
  });
  pagoCompleto = computed(() => this.totalPagado() >= this.total());

  vuelto = computed(() => {
    const vuelto = redondearDosDecimales(this.totalPagado() - this.total());
    return vuelto > 0 ? vuelto : 0;
  });

  agregarMetodoPago($event: IAgregarPagoComponent) {
    if (!$event.tipo_pago) return;
    this.formProcesarVenta().controlValue().tipoPago = $event.tipo_pago;
    this.formProcesarVenta().controlValue().montoTipoPago = $event.monto;

    if (
      this.formProcesarVenta().controlValue().tipoPago &&
      this.formProcesarVenta().controlValue().montoTipoPago > 0
    ) {
      this.pagosRealizados.update((pagos) => [
        ...pagos,
        {
          tipo_pago: this.formProcesarVenta().controlValue().tipoPago,
          monto: this.formProcesarVenta().controlValue().montoTipoPago,
        },
      ]);
      this.formProcesarVenta().controlValue().montoTipoPago = 0;
    }
  }
  eliminarMetodoPago(index: number) {
    this.pagosRealizados.update((pagos) => pagos.filter((_, idx) => idx !== index));
  }
  // cliente
  onClientSelect(cliente: IClienteData | null) {
    this.formProcesarVenta().controlValue().cliente = cliente;
    if (cliente?.numero_celular) {
      this.sendWhatsApp.set(true);
    } else {
      this.sendWhatsApp.set(false);
    }
  }
  //#endregion

  //#region idempotencia
  private obtenerOGenerarIdempotencyKey(): string {
    const existing = this.idempotencyKey();
    if (existing) return existing;
    const key = crypto.randomUUID();
    this.idempotencyKey.set(key);
    try {
      sessionStorage.setItem(this.STORAGE_KEY_PENDING_SALE, JSON.stringify({
        key,
        timestamp: Date.now(),
        fingerprint: this.calcularFingerprintCarrito(),
      }));
    } catch { /* sessionStorage no disponible */ }
    return key;
  }

  private limpiarIdempotencyKey() {
    this.idempotencyKey.set(null);
    try {
      sessionStorage.removeItem(this.STORAGE_KEY_PENDING_SALE);
    } catch { /* sessionStorage no disponible */ }
  }

  private calcularFingerprintCarrito(): string {
    return this.productosCarrito
      .map((p) => `${p.id}:${p.cantidad}`)
      .sort()
      .join(',');
  }

  private restaurarIdempotencyKeyDeSession() {
    try {
      const stored = sessionStorage.getItem(this.STORAGE_KEY_PENDING_SALE);
      if (!stored) return;
      const { key, timestamp, fingerprint } = JSON.parse(stored) as {
        key: string;
        timestamp: number;
        fingerprint: string;
      };
      if (Date.now() - timestamp < this.MAX_KEY_AGE_MS) {
        const currentFingerprint = this.calcularFingerprintCarrito();
        if (!fingerprint || fingerprint === currentFingerprint) {
          this.idempotencyKey.set(key);
        } else {
          sessionStorage.removeItem(this.STORAGE_KEY_PENDING_SALE);
        }
      } else {
        sessionStorage.removeItem(this.STORAGE_KEY_PENDING_SALE);
      }
    } catch {
      try { sessionStorage.removeItem(this.STORAGE_KEY_PENDING_SALE); } catch { /* limpio */ }
    }
  }
  //#endregion

  //#region acciones de venta
  private ventaApiService = inject(VentaApiService);
  private creditoApiService = inject(CreditoApiService);
  ventaCredito() {
    if (this.procesandoVenta()) return;
    if (this.formProcesarVenta().controlValue().cliente == null) {
      this.toastService.show(
        'top-right',
        'error',
        'Error',
        'Debe seleccionar un cliente para venta a crédito.',
      );
      return;
    }
    const { observaciones, cliente } = this.formProcesarVenta().controlValue();
    const pagosRealizados = this.pagosRealizados();
    const req: IRealizarVentaCreditoReq = {
      idempotency_key: this.obtenerOGenerarIdempotencyKey(),
      cliente_id: cliente ? cliente.id : 0,
      cliente_dniRuc: cliente ? cliente.dniRuc : '99999999',
      cliente_nombre_completo: cliente ? cliente.nombre_completo : 'CLIENTE VARIOS',
      igv_porcentaje: this.igvPorcentaje(),
      pagosRealizados: pagosRealizados.map((pago) => ({
        id: pago.tipo_pago.id,
        nombre: pago.tipo_pago.nombre,
        monto: pago.monto,
        tipo_transaccion: pago.tipo_pago.tipo_transaccion,
      })),
      productosComprados: this.productosCarrito.map(buildProductoCarritoToProductoComprado),
      descuento: this.descuentoFinal() == 0 ? undefined : this.descuentoFinal(),
      observaciones: observaciones || undefined,
      mesa_id: this.mesaId(),
      gestion_tiempo_id: this.gestionTiempoId(),
    };
    if (this.sendWhatsApp()) {
      req.send_whatsapp = true;
      if (this.disableWhatsApp()) {
        req.whatsapp_number = `${this.whatsAppCountryCode()}${this.whatsAppNumber()}`;
      } else {
        req.whatsapp_number =
          this.formProcesarVenta().controlValue().cliente?.numero_celular || undefined;
      }
    }
    this.procesandoVenta.set(true);
    this.creditoApiService.realizarVentaCredito(req).subscribe({
      next: () => {
        this.procesandoVenta.set(false);
        this.limpiarIdempotencyKey();
        this.toastService.show(
          'top-right',
          'success',
          'Éxito',
          'Venta a crédito realizada con éxito.',
        );
        this.cerrarModal();
        this.carritoCompraService.limpiar();
        this.emitirVenta.emit();
        this.restartForm();
      },
      error: (err) => {
        this.procesandoVenta.set(false);
        if (err.status == 401) return;
        if (err.status == 409 && err.error?.duplicado) {
          this.limpiarIdempotencyKey();
          this.toastService.show(
            'top-right',
            'warn',
            'Ya procesada',
            'Esta venta ya fue registrada anteriormente.',
          );
          this.cerrarModal();
          this.carritoCompraService.limpiar();
          this.emitirVenta.emit();
          this.restartForm();
          return;
        }
        this.toastService.show(
          'top-right',
          'error',
          'Error',
          err.error.message ? formatearErrorStock(err.error.message) : 'Error al procesar la venta a crédito.',
        );
      },
    });
  }
  finalizarVenta() {
    if (this.procesandoVenta()) return;
    if (!this.pagoCompleto()) {
      this.toastService.show('top-right', 'error', 'Error', 'El monto pagado es insuficiente.');
      return;
    }
    const { tipo_documento, observaciones, cliente } = this.formProcesarVenta().controlValue();
    const pagosRealizados = this.pagosRealizados();

    const req: IRealizarVentaReq = {
      idempotency_key: this.obtenerOGenerarIdempotencyKey(),
      cliente_id: cliente ? cliente.id : 0,
      cliente_dniRuc: cliente ? cliente.dniRuc : '99999999',
      cliente_nombre_completo: cliente ? cliente.nombre_completo : 'CLIENTE VARIOS',
      igv_porcentaje: this.igvPorcentaje(),
      pagosRealizados: pagosRealizados.map((pago) => ({
        id: pago.tipo_pago.id,
        nombre: pago.tipo_pago.nombre,
        monto: pago.monto,
        tipo_transaccion: pago.tipo_pago.tipo_transaccion,
      })),
      productosComprados: this.productosCarrito.map(buildProductoCarritoToProductoComprado),
      tipo_documento_sunat: tipo_documento,
      descuento: this.descuentoFinal() == 0 ? undefined : this.descuentoFinal(),
      observaciones: observaciones || undefined,

      mesa_id: this.mesaId(),
      gestion_tiempo_id: this.gestionTiempoId(),
    };
    if (this.sendWhatsApp()) {
      req.send_whatsapp = true;
      if (this.disableWhatsApp()) {
        req.whatsapp_number = `${this.whatsAppCountryCode()}${this.whatsAppNumber()}`;
      } else {
        req.whatsapp_number =
          this.formProcesarVenta().controlValue().cliente?.numero_celular || undefined;
      }
    }
    this.procesandoVenta.set(true);
    this.ventaApiService.realizarVenta(req).subscribe({
      next: (ventaData) => {
        this.procesandoVenta.set(false);
        this.limpiarIdempotencyKey();
        void this.imprimirVenta(ventaData);
        this.toastService.show('top-right', 'success', 'Éxito', 'Venta realizada con éxito.');
        this.cerrarModal();
        this.carritoCompraService.limpiar();
        this.emitirVenta.emit();
        this.restartForm();
      },
      error: (err) => {
        this.procesandoVenta.set(false);
        console.error('Error al procesar la venta:', err);
        if (err.status == 401) return;
        if (err.status == 409 && err.error?.duplicado) {
          this.limpiarIdempotencyKey();
          this.toastService.show(
            'top-right',
            'warn',
            'Ya procesada',
            'Esta venta ya fue registrada anteriormente.',
          );
          this.cerrarModal();
          this.carritoCompraService.limpiar();
          this.emitirVenta.emit();
          this.restartForm();
          return;
        }
        this.toastService.show(
          'top-right',
          'error',
          'Error',
          err.error.message ? formatearErrorStock(err.error.message) : 'Error al procesar la venta.',
        );
      },
    });
  }
  closedModal = output<void>();
  cerrarModal() {
    if (this.procesandoVenta()) {
      this.toastService.show(
        'top-right',
        'warn',
        'En proceso',
        'No se puede cerrar el modal mientras se procesa la venta.',
      );
      return;
    }
    this.closedModal.emit();
  }
  //#endregion

  //#region Inyección de servicios
  private carritoCompraService = inject(CarritoCompraService);
  private tipoPagoService = inject(TipoPagoService);
  //#endregion
  ngOnInit() {
    this.restaurarIdempotencyKeyDeSession();
    this.carritoCompraService.productosCarritoCompra.subscribe((productos) => {
      this.productosCarrito = productos;
    });
    this.tipoPagoService.listTiposPago.subscribe((tiposPago) => {
      this.tiposPago = tiposPago;
    });
    this.impresoraService.impresoraSeleccionada$.subscribe((id) => {
      this.impresoraSeleccionada.set(id);
    });

    this.cargarUltimoIGV();
  }

  emitirVenta = output<void>();

  restartForm() {
    this.limpiarIdempotencyKey();
    this.formProcesarVenta().controlValue().observaciones = '';
    this.descuento.set({ valor: 0, efectivo: false });
    this.formProcesarVenta().controlValue().tipo_documento = ETipoDocumentoSUNAT.NOTA_VENTA;
    this.pagosRealizados.set([]);
    this.formProcesarVenta().controlValue().tipoPago = {} as ITipoPagoData;
    this.formProcesarVenta().controlValue().montoTipoPago = 0;
    this.formProcesarVenta().controlValue().cliente = null;
    this.sendWhatsApp.set(false);
    this.whatsAppCountryCode.set('+51');
    this.whatsAppNumber.set('');
  }

  private async imprimirVenta(venta: IVentaData) {
    const id_impresora = this.impresoraSeleccionada();
    const datos: ImpresoraDatosVentaRq = {
      documento_sunat: {
        tipo_documento_sunat: venta.tipo_documento_sunat,
        serie_documento: venta.serie_documento,
        numero_documento: venta.correlativo_documento,
      },
      cliente: {
        nombre_completo: venta.snapshot_cliente_nombre,
        dniRuc: venta.snapshot_cliente_documento,
      },
      productos_comprados: venta.detalles.map((prod: any) => ({
        id: prod.producto_id,
        nombre: prod.producto_nombre,
        codigo: prod.producto_codigo ?? null,
        cantidad: prod.cantidad,
        precio_unitario: prod.precio_unitario,
        descuento_valor_aplicado: (prod as any).descuento_valor_aplicado ?? 0,
      })),
      igv_porcentaje: venta.igv_porcentaje,
      total: venta.total,
      descuento: venta.descuento,
      fecha_venta: venta.created_at,
    };

    if (id_impresora === null || id_impresora === undefined) {
      return;
    }

    if (id_impresora === ID_DESCARGA_PDF) {
      const empresaData = this.empresaService.getEmpresaData();
      if (!empresaData) {
        this.toastService.show(
          'top-right',
          'error',
          'Error',
          'No se pudo obtener la información de la empresa para generar el PDF.',
        );
        return;
      }
      const sucursal = this.sucursalService.activeSucursal() ?? null;
      const footerMensaje = obtenerFooterMensajePorDocumento(sucursal, venta.tipo_documento_sunat);
      const htmlContent = await buildImpresion(
        empresaData,
        ETipoImpresion.VENTA,
        datos as unknown as Record<string, unknown>,
        footerMensaje,
      );
      const filename = `venta-${venta.serie_documento}${venta.correlativo_documento.toString().padStart(8, '0')}`;
      await this.qzService.printHtmlWithBrowserDialog(htmlContent, filename, 80);
      return;
    }

    const printData: CreatePrintJobRq = {
      id_config_impresora: id_impresora,
      tipo_impresion: ETipoImpresion.VENTA,
      datos,
    };
    logger('Enviando a imprimir con datos:', printData);
    this.impresoraApiService.imprimir(printData).subscribe({
      next: () => {
        this.toastService.show(
          'top-right',
          'success',
          'Impresión enviada',
          'La venta se ha enviado a imprimir correctamente.',
        );
      },
      error: (err) => {
        console.error('Error al enviar a imprimir:', err);
        this.toastService.show(
          'top-right',
          'error',
          'Error',
          err.error.message || 'Error al enviar la impresión.',
        );
      },
    });
  }
  onChangeIgvPorcentaje(nuevoValor: number) {
    this.igvPorcentaje.set(nuevoValor);
    this.sucursalService.guardarUltimoIGV(nuevoValor);
  }
  private cargarUltimoIGV() {
    // Cargar el último IGV utilizado al iniciar el componente
    const ultimoIGV = this.sucursalService.getUltimoIGV();
    if (ultimoIGV !== null) {
      this.igvPorcentaje.set(ultimoIGV);
    } else {
      // Si no hay IGV guardado, usar el valor predeterminado
      this.igvPorcentaje.set(0.18);
      this.sucursalService.guardarUltimoIGV(0.18);
    }
  }
  onClickDescuentoEfectivo(esEfectivo: boolean) {
    this.descuento.update((desc) => ({
      valor: desc.valor,
      efectivo: esEfectivo,
    }));
  }
}
